/*  MUI  */
import { CheckCircleOutline } from '@mui/icons-material';
import {
    Alert,
    AlertTitle,
    Button,
    Checkbox,
    Grid,
    Paper,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { ApplicationNamePartnerAppModalCell } from 'application/Shared/Components/ApplicationNamePartnerAppModalCell';
import { SSOIcons } from 'application/Shared/Components/Avatars/SSO';
import {
    BusinessOwnerPicker,
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { ShowPartnerTopInfoBanner } from 'application/Shared/Components/ShowPartnerTopInfoBanner';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Rank } from 'application/Shared/Components/Usage';
import { Helpers, Notify, TableHeaders } from 'application/Shared/Helpers';
import { BulkClassifyModal, PartnerAppModal } from 'application/Shared/Modals';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import { Application } from 'objects/Application';
import NotesModal from 'objects/Application/NotesModal';
import Owner from 'objects/functions/BusinessOwner';

/*  React  */
import React, { Component, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';

const Actions = ({
    checkedMap,
    checkedCount,
    row,
    setBulkEdit,
    setViewing
}) => {
    const { t } = useTranslation();
    const { applicationId } = row;

    const checked = Helpers.isset(checkedMap, applicationId);

    return (
        <Button
            disabled={!checked && checkedCount > 0}
            data-trackid="viewAction"
            onClick={() => {
                if (!checked) {
                    setViewing(row);
                    return;
                }

                setBulkEdit(true);
            }}
            variant={checked ? 'outlined' : 'contained'}
            color="primary"
        >
            {!checked ? (
                t('Common.view')
            ) : (
                <>
                    {t('Common.classify')} {checkedCount}{' '}
                    {checkedCount === 1 ? t('Common.app') : t('Common.apps')}
                </>
            )}
        </Button>
    );
};

const RenderNotes = ({ reloadFn, row }) => {
    const { application, communityNotes, partnerNotes } = row;

    return (
        <NotesModal
            application={application}
            communityNotes={communityNotes}
            partnerNotes={partnerNotes}
            partner
            partnerHub
            onSave={(reload) => {
                if (reload) reloadFn();
            }}
        />
    );
};

const RenderRow = ({
    checkedCount,
    checkedMap,
    handleBusinessOwnerChange,
    handleLifecycle,
    handleRisk,
    reloadFn,
    rerenderMap,
    row,
    rowMap,
    setBulkEdit,
    setCheckedCount,
    setCheckedMap,
    setViewing
}) => {
    const { t } = useTranslation();
    const { applicationId } = row;
    const currentRow = rowMap[applicationId];
    const currentCheckedMap = { ...checkedMap };
    let currentCheckedCount = checkedCount;
    const rerender = Helpers.isset(rerenderMap, applicationId);

    if (!currentRow) {
        return null;
    }

    const {
        id,
        application,
        status,
        businessOwnerConfiguration,
        totalUsage,
        ranking,
        risk,
        defaultSet
    } = currentRow;
    const checked = Helpers.isset(checkedMap, applicationId);

    return (
        <TableRow key={`row_${id}`}>
            <TableCell style={{ padding: 0, textAlign: 'center' }}>
                <Checkbox
                    checked={checked}
                    onChange={(e) => {
                        if (e.target.checked) {
                            currentCheckedMap[applicationId] = true;
                            currentCheckedCount += 1;
                        } else {
                            delete currentCheckedMap[applicationId];
                            currentCheckedCount -= 1;
                        }
                        setCheckedCount(currentCheckedCount);
                        setCheckedMap(currentCheckedMap);
                    }}
                />
            </TableCell>
            <TableCell align="center">
                <Rank usage={totalUsage} ranking={ranking} />
            </TableCell>
            <TableCell>
                <Grid container alignItems="center">
                    <Grid item xs={1}>
                        {defaultSet && <CheckCircleOutline color="success" />}
                    </Grid>
                    <Grid item xs={10}>
                        <Application data={application}>
                            <ApplicationNamePartnerAppModalCell
                                data={currentRow}
                                nameClassname="link bold underline"
                                onClose={reloadFn}
                                withCategory
                            />
                        </Application>
                    </Grid>
                    <Grid item xs={1}>
                        <RenderNotes reloadFn={reloadFn} row={currentRow} />
                    </Grid>
                </Grid>
            </TableCell>
            <TableCell align="center">{currentRow.clientsUsing}</TableCell>
            <TableCell>
                <SSOIcons data={currentRow} noCount={false} />
            </TableCell>
            <TableCell>
                <RiskPicker
                    disabled={checked}
                    noFullWidth
                    unselectedOption="No Default"
                    unselectedValue={null}
                    value={risk}
                    onChange={(v) => handleRisk({ risk: v, row: currentRow })}
                    rerender={rerender}
                />
            </TableCell>
            <TableCell>
                <LifecyclePicker
                    disabled={checked}
                    multiple={false}
                    noFullWidth
                    disableAllOption
                    value={status}
                    onChange={(v) =>
                        handleLifecycle({ row: currentRow, status: v })
                    }
                    rerender={rerender}
                />
            </TableCell>
            <TableCell>
                <BusinessOwnerPicker
                    disabled={checked}
                    multiple={false}
                    noFullWidth
                    value={
                        businessOwnerConfiguration.length
                            ? businessOwnerConfiguration
                            : 'notAssigned'
                    }
                    unselectedValue="notAssigned"
                    unselectedOption={t(
                        'ManageApplicationAdmin.no_user_selected'
                    )}
                    hideUsersInOrganization
                    noBusinessOwnerRequired={
                        businessOwnerConfiguration === 'noBusinessOwnerRequired'
                    }
                    managedByPartner={
                        businessOwnerConfiguration === 'managedByPartner'
                    }
                    managed3rdParty={
                        businessOwnerConfiguration === 'managed3rdParty'
                    }
                    onChange={(businessOwnerValue) =>
                        handleBusinessOwnerChange(
                            applicationId,
                            businessOwnerValue
                        )
                    }
                    rerender={rerender}
                />
            </TableCell>
            <TableCell align="center">
                <Actions
                    checkedMap={checkedMap}
                    checkedCount={checkedCount}
                    row={currentRow}
                    setBulkEdit={setBulkEdit}
                    setViewing={setViewing}
                />
            </TableCell>
        </TableRow>
    );
};

const csvFields = [
    { key: 'application.name', label: 'Application Name' },
    {
        key: 'application.category',
        label: 'Application Category'
    },
    {
        key: 'application.subCategory',
        label: 'Application Sub-Category'
    },
    { key: 'application.name', label: 'Application Name' },
    { key: 'application.risk', label: 'Application Risk' },
    { key: 'status', label: 'Default Lifecycle Stage' },
    {
        key: 'businessOwnerConfiguration',
        label: 'Default Business Owner'
    }
];

const searchFields = [
    'applicationId',
    'application.name',
    'application.category',
    'application.subCategory'
];

const PartnerAppsTable = ({
    checkedCount,
    checkedMap,
    handleBusinessOwnerChange,
    handleLifecycle,
    handleRisk,
    handleRows,
    handleSelectAll,
    reloadFn,
    rerenderMap,
    rowMap,
    setBulkEdit,
    setCheckedCount,
    setCheckedMap,
    setViewing
}) => {
    const { t } = useTranslation();

    const headers = useMemo(
        () =>
            new TableHeaders({ t }).make([
                [
                    'Common.rank',
                    {
                        align: 'center',
                        sortable: true,
                        sortableFieldType: 'number',
                        sortableKey: 'totalUsage',
                        squish: true,
                        width: '5%'
                    }
                ],
                [
                    'Common.application',
                    {
                        sortable: true,
                        sortableFieldType: 'string',
                        sortableKey: 'application.name',
                        width: '20%'
                    }
                ],
                [
                    'Common.client_count',
                    {
                        align: 'center',
                        sortable: true,
                        sortableFieldType: 'number',
                        sortableKey: 'clientsUsing',
                        squish: true,
                        width: '10%'
                    }
                ],
                { component: 'SSO', width: '10%' },
                { component: 'Risk', width: '10%' },
                [
                    'Common.lifecycleStage',
                    {
                        sortable: true,
                        sortableFieldType: 'string',
                        sortableKey: 'status',
                        width: '15%'
                    }
                ],
                [
                    'Common.business_owner',
                    {
                        sortable: true,
                        sortableFieldType: 'string',
                        sortableKey: 'businessOwnerConfiguration',
                        width: '15%'
                    }
                ],
                [
                    'Common.actions',
                    { align: 'right', sortable: false, width: '10%' }
                ]
            ]),
        [t]
    );

    const toggles = useMemo(
        () => [
            {
                defaultEnabled: true,
                key: 'excludePersonal',
                parameter: { field: 'excludePersonal', value: true },
                toggleText: t('Common.exclude_personal_applications')
            },
            {
                defaultEnabled: false,
                key: 'excludeDefaultSet',
                parameter: { field: 'excludeDefaultSet', value: true },
                toggleText: t('Toggle.excludeDefaultSet')
            }
        ],
        [t]
    );

    return (
        <ManagedTable
            csvExport
            csvExportFields={csvFields}
            csvExportFileName="application_stack"
            data-trackid="applicationStack"
            handleRows={handleRows}
            handleSelectAll={handleSelectAll}
            headers={headers}
            manageRows
            renderBody={(row) => (
                <RenderRow
                    checkedCount={checkedCount}
                    checkedMap={checkedMap}
                    handleBusinessOwnerChange={handleBusinessOwnerChange}
                    handleLifecycle={handleLifecycle}
                    handleRisk={handleRisk}
                    reloadFn={reloadFn}
                    rerenderMap={rerenderMap}
                    row={row}
                    rowMap={rowMap}
                    setBulkEdit={setBulkEdit}
                    setCheckedCount={setCheckedCount}
                    setCheckedMap={setCheckedMap}
                    setViewing={setViewing}
                />
            )}
            renderBodyKey="id"
            renderFooter
            rowsApi="/appDefault/aggregate"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            searchFields={searchFields}
            sortDirection="DESC"
            sortFieldType="number"
            sortKey="totalUsage"
            toggles={toggles}
        />
    );
};

/*  State  */
const state = () => ({
    bulkEdit: false,
    checkedCount: 0,
    checkedMap: {},
    csvFields: null,
    headers: null,
    loaded: false,
    noResults: false,
    rerenderMap: {},
    rowMap: {},
    rows: [],
    selectAll: false,
    tableLoaded: false,
    toggles: null,
    viewing: false
});

class PartnerApps extends Component {
    constructor(props) {
        super(props);
        this.state = state();

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate() {
        const { reload } = this.props;
        if (reload) {
            this.reload();
        }
    }

    componentWillUnmount() {
        this.unmount = true;
        this.requests.cancel();
    }

    load = () => {
        this.setState({
            loaded: true
        });
    };

    reload = () => {
        if (this.unmount) {
            return;
        }

        this.setState(state(), () => this.load());
    };

    handleRisk = async (p) => {
        const { t } = this.props;
        const { rowMap } = this.state;
        const { row, risk } = p;
        const { applicationId } = row;

        try {
            // Update the Partner Application Link Settings instead
            await axios.post('/appDefault', { applicationId, risk });
            this.setState({
                rowMap: {
                    ...rowMap,
                    [applicationId]: { ...row, defaultSet: true, risk }
                }
            });
            Notify.s(t('Classify.riskSet'));
        } catch (e) {
            debug.e('Unable to save App Risk', e);
            Notify.e(t('Common.error'));
        }
    };

    handleLifecycle = async (p) => {
        const { t } = this.props;
        const { rowMap } = this.state;
        const { row, status } = p;
        const { applicationId } = row;

        try {
            // Update the Partner Application Link Settings instead
            await axios.post('/appDefault', { applicationId, status });
            this.setState({
                rowMap: {
                    ...rowMap,
                    [applicationId]: { ...row, defaultSet: true, status }
                }
            });
            Notify.s(t('Classify.statusSet'));
        } catch (e) {
            debug.e('Unable to save App Lifecycle', e);
            Notify.e(t('Common.error'));
        }
    };

    handleBusinessOwnerChange = async (applicationId, businessOwner) => {
        const { t } = this.props;

        try {
            const owner = Owner.handleValues(businessOwner);
            const {
                noBusinessOwnerRequired,
                managedByPartner,
                managed3rdParty
            } = owner;
            let businessOwnerConfiguration;

            if (noBusinessOwnerRequired) {
                businessOwnerConfiguration = 'noBusinessOwnerRequired';
            } else if (managedByPartner) {
                businessOwnerConfiguration = 'managedByPartner';
            } else if (managed3rdParty) {
                businessOwnerConfiguration = 'managed3rdParty';
            } else {
                businessOwnerConfiguration = '';
            }

            this.updateRows(applicationId, 'businessOwner', businessOwner);
            this.updateRows(
                applicationId,
                'businessOwnerConfiguration',
                businessOwnerConfiguration
            );
            await axios.post('/appDefault', {
                applicationId,
                businessOwnerConfiguration
            });
            this.updatedRow();
            Notify.s(t('Classify.businessOwnerSet'));
        } catch (e) {
            debug.e('handleBusinessOwnerChange', e);
            Notify.e(t('Common.error'));
        }
    };

    updateRows = (applicationId, key, value) => {
        if (this.unmount) {
            return;
        }

        const { rows } = this.state;

        this.setState({
            rows: rows.map((row) => {
                const newRow = row;
                if (row.applicationId === applicationId) {
                    newRow[key] = value;
                }
                return {
                    ...newRow,
                    updating: newRow.applicationId === applicationId
                };
            })
        });
    };

    updatedRow = () => {
        if (this.unmount) {
            return;
        }

        const { rows } = this.state;
        this.setState({
            rows: rows.map((row) => ({ ...row, updating: false }))
        });
    };

    handleSelectAll = (checked) => {
        if (this.unmount) {
            return;
        }

        const { rows } = this.state;

        if (!Array.isArray(rows)) {
            return;
        }

        if (!checked) {
            this.setState({
                checkedCount: 0,
                checkedMap: {},
                selectAll: checked
            });
            return;
        }

        this.setState({
            selectAll: checked
        });
        this.rerenderChecked();
    };

    rerenderChecked = () => {
        const { rows } = this.state;

        let checkedCount = 0;
        const checkedMap = {};

        rows.forEach((row) => {
            const { applicationId } = row;
            if (!applicationId) {
                return;
            }

            if (!checkedMap[applicationId]) {
                checkedMap[applicationId] = true;
                checkedCount += 1;
            }
        });

        this.setState({ checkedCount, checkedMap });
    };

    handleRows = (rows) => {
        if (this.unmount) {
            return;
        }

        const { rowMap, tableLoaded } = this.state;

        if (!rows?.length || !tableLoaded) {
            this.setState({ noResults: true, tableLoaded: true });
            return;
        }

        rows.forEach((r) => {
            rowMap[r.applicationId] = r;
        });

        this.setState(
            {
                noResults: !rows.length,
                rowMap,
                rows,
                tableLoaded: true
            },
            () => {
                if (this.state.selectAll) this.rerenderChecked();
            }
        );
    };

    renderBulkEdit = () => {
        const { bulkEdit, checkedMap, rowMap } = this.state;

        if (!bulkEdit) {
            return null;
        }

        const ids = Object.keys(checkedMap);

        return (
            <BulkClassifyModal
                open={bulkEdit}
                applications={ids}
                rowMap={rowMap}
                appDefaults
                closeModal={() => {
                    this.setState({
                        bulkEdit: false
                    });
                }}
                onSave={(p) => {
                    this.setState(
                        {
                            bulkEdit: false,
                            checkedCount: 0,
                            checkedMap: {}
                        },
                        () => {
                            this.handleSelectAll(false);
                        }
                    );

                    if (!p) {
                        return;
                    }

                    if (p.rowMap) {
                        this.setState(
                            { rerenderMap: checkedMap, rowMap: p.rowMap },
                            () => {
                                this.setState({ rerenderMap: false });
                            }
                        );
                    }
                }}
            />
        );
    };

    renderAppModal = () => {
        const { viewing } = this.state;

        if (!viewing) {
            return null;
        }

        return (
            <PartnerAppModal
                open={!!viewing}
                data={viewing}
                onCloseModal={(isChanged) => {
                    if (isChanged) {
                        this.reload();
                    }
                    this.setState({ viewing: false });
                }}
            />
        );
    };

    render() {
        const { t } = this.props;
        const {
            bulkEdit,
            checkedCount,
            checkedMap,
            loaded,
            noResults,
            rerenderMap,
            rowMap,
            selectAll,
            tableLoaded,
            viewing
        } = this.state;

        return (
            <>
                <ShowPartnerTopInfoBanner type="showMeHowToDeploy" />
                <PageTitle title={t('Common.application_stack')} />
                {bulkEdit && this.renderBulkEdit()}
                {viewing && this.renderAppModal()}
                <Paper elevation={0} variant="outlined">
                    <Grid item xs={12}>
                        <Typography variant="p">
                            {t('PartnerApplicationSettings.description')}
                        </Typography>
                        {tableLoaded && noResults && (
                            <Alert severity="info" icon={false}>
                                <AlertTitle>
                                    {t(
                                        'PartnerApplicationSettings.alert_title'
                                    )}
                                </AlertTitle>
                                {t('PartnerApplicationSettings.alert_text')}
                            </Alert>
                        )}

                        {loaded && (
                            <PartnerAppsTable
                                checkedCount={checkedCount}
                                checkedMap={checkedMap}
                                handleBusinessOwnerChange={
                                    this.handleBusinessOwnerChange
                                }
                                handleLifecycle={this.handleLifecycle}
                                handleRisk={this.handleRisk}
                                handleRows={this.handleRows}
                                handleSelectAll={this.handleSelectAll}
                                reloadFn={this.reload}
                                rerenderMap={rerenderMap}
                                rowMap={rowMap}
                                selectAll={selectAll}
                                setBulkEdit={(value) =>
                                    this.setState({ bulkEdit: value })
                                }
                                setCheckedCount={(value) =>
                                    this.setState({ checkedCount: value })
                                }
                                setCheckedMap={(value) =>
                                    this.setState({ checkedMap: value })
                                }
                                setViewing={(value) =>
                                    this.setState({ viewing: value })
                                }
                            />
                        )}
                    </Grid>
                </Paper>
            </>
        );
    }
}
export default withTranslation()(PartnerApps);
